/**
 * Shared utility functions for mutations
 * Extracted to remove code duplication between video and image mutations
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Helper to update a single item by ID
 */
export const createItemUpdater = (itemId: string, updates: Record<string, any>) => {
  return (item: any) => 
    item.id === itemId 
      ? { ...item, ...updates, updated_at: new Date().toISOString() }
      : item;
};

/**
 * Helper to update items in various data formats (infinite query, list, pinned)
 */
export const updateItemInData = (itemId: string, updates: Record<string, any>) => {
  const updateSingleItem = createItemUpdater(itemId, updates);
  
  return (oldData: any) => {
    if (!oldData) return oldData;
    
    if (oldData.pages) {
      // Infinite query format
      return {
        ...oldData,
        pages: oldData.pages.map((page: any) => ({
          ...page,
          videos: page.videos?.map(updateSingleItem),
          images: page.images?.map(updateSingleItem),
        })),
      };
    } else if (oldData.videos) {
      // Regular video list format
      return {
        ...oldData,
        videos: oldData.videos.map(updateSingleItem),
      };
    } else if (oldData.images) {
      // Regular image list format
      return {
        ...oldData,
        images: oldData.images.map(updateSingleItem),
      };
    } else if (oldData.pinned_videos) {
      // Pinned videos format
      return {
        ...oldData,
        pinned_videos: oldData.pinned_videos.map(updateSingleItem),
      };
    } else if (Array.isArray(oldData)) {
      // Direct array format (for pinned videos that return array directly)
      return oldData.map(updateSingleItem);
    }
    return oldData;
  };
};

/**
 * Cancel all queries matching a predicate
 */
export const cancelQueriesForResource = async (
  queryClient: QueryClient,
  resourceType: string
) => {
  await queryClient.cancelQueries({ 
    predicate: (query) => {
      return query.queryKey.length > 0 && query.queryKey[0] === resourceType;
    }
  });
};

/**
 * Get snapshot of all queries for a resource
 */
export const getQueriesSnapshot = (
  queryClient: QueryClient,
  resourceType: string
) => {
  return queryClient.getQueriesData({ 
    predicate: (query) => {
      return query.queryKey.length > 0 && query.queryKey[0] === resourceType;
    }
  });
};

/**
 * Apply optimistic update to all queries for a resource
 */
export const applyOptimisticUpdate = (
  queryClient: QueryClient,
  resourceType: string,
  updater: (oldData: any) => any
) => {
  queryClient.setQueriesData({ 
    predicate: (query) => {
      return query.queryKey.length > 0 && query.queryKey[0] === resourceType;
    }
  }, updater);
};

/**
 * Rollback queries to previous state
 */
export const rollbackQueries = (
  queryClient: QueryClient,
  previousData: [any, any][]
) => {
  for (const [queryKey, data] of previousData) {
    queryClient.setQueryData(queryKey, data);
  }
};
